import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import SEO from "../../components/site/SEO";
import { inputCls, labelCls, btnPrimaire, dateFr } from "./ui";

export default function AdminHoraires() {
  const [jours, setJours] = useState([]);
  const [fermetures, setFermetures] = useState([]);
  const [nouvelle, setNouvelle] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/site/config")
      .then((r) => {
        setJours(r.data.horaires.jours || []);
        setFermetures(r.data.horaires.fermetures || []);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const enregistrer = async (ferm = fermetures) => {
    try {
      await api.put("/admin/horaires", { jours, fermetures: ferm });
      toast.success("Les horaires ont été enregistrés sur le site.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const ajouterFermeture = async (e) => {
    e.preventDefault();
    const ferm = [...fermetures, { ...nouvelle, id: "" }];
    setFermetures(ferm);
    setNouvelle(null);
    await enregistrer(ferm);
    toast.success("Fermeture signalée. Le bandeau d'information s'affichera automatiquement pendant cette période.");
  };

  const retirerFermeture = async (id) => {
    if (!window.confirm("Retirer cette fermeture exceptionnelle du site ?")) return;
    const ferm = fermetures.filter((f) => f.id !== id);
    setFermetures(ferm);
    await enregistrer(ferm);
  };

  if (chargement) return <p className="text-brand-body text-lg">Chargement…</p>;

  return (
    <div data-testid="admin-horaires" className="max-w-3xl">
      <SEO title="Horaires — Espace gérant | Promo Pneus" description="Modifier les horaires et signaler une fermeture exceptionnelle." />
      <h1 className="font-heading font-bold uppercase text-4xl text-brand-dark tracking-tight mb-8">Horaires</h1>

      <div className="bg-white p-6 shadow-sm mb-10">
        <h2 className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-5">Horaires habituels</h2>
        <div className="space-y-4">
          {jours.map((j, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`jour-${i}`} className={labelCls}>Jour(s)</label>
                <input id={`jour-${i}`} data-testid={`horaire-jour-${i}`} className={inputCls} value={j.jour}
                  onChange={(e) => setJours(jours.map((x, k) => (k === i ? { ...x, jour: e.target.value } : x)))} />
              </div>
              <div>
                <label htmlFor={`horaires-${i}`} className={labelCls}>Horaires</label>
                <input id={`horaires-${i}`} data-testid={`horaire-valeur-${i}`} className={inputCls} value={j.horaires}
                  onChange={(e) => setJours(jours.map((x, k) => (k === i ? { ...x, horaires: e.target.value } : x)))}
                  placeholder="Ex. : 9h – 12h · 14h – 18h" />
              </div>
            </div>
          ))}
        </div>
        <button data-testid="horaires-enregistrer" onClick={() => enregistrer()} className={`${btnPrimaire} mt-6`}>
          Enregistrer les horaires
        </button>
      </div>

      <div className="bg-white p-6 shadow-sm">
        <h2 className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-3">Fermetures exceptionnelles</h2>
        <p className="text-brand-body mb-5">
          Pendant la période indiquée, un bandeau d'information s'affiche automatiquement en haut du site et sur la page Contact.
          Il disparaît tout seul à la fin de la période.
        </p>
        {fermetures.length === 0 && !nouvelle && (
          <p className="text-brand-body mb-5" data-testid="fermetures-vide">Aucune fermeture signalée.</p>
        )}
        <div className="space-y-3 mb-6">
          {fermetures.map((f) => (
            <div key={f.id} data-testid={`fermeture-ligne-${f.id}`}
              className="flex items-center justify-between gap-3 bg-brand-light px-4 py-3.5">
              <p className="font-body text-brand-dark">
                Du <strong>{dateFr(f.debut)}</strong> au <strong>{dateFr(f.fin)}</strong>
                {f.motif ? ` — ${f.motif}` : ""}
              </p>
              <button data-testid={`fermeture-supprimer-${f.id}`} onClick={() => retirerFermeture(f.id)}
                className="p-3 text-brand-redhover hover:bg-red-100 transition-colors" aria-label="Retirer cette fermeture">
                <Trash2 size={20} />
              </button>
            </div>
          ))}
        </div>

        {nouvelle ? (
          <form onSubmit={ajouterFermeture} className="space-y-4 bg-brand-light p-5" data-testid="fermeture-formulaire">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fermeture-debut" className={labelCls}>Premier jour de fermeture</label>
                <input id="fermeture-debut" data-testid="fermeture-debut" type="date" required className={inputCls}
                  value={nouvelle.debut} onChange={(e) => setNouvelle({ ...nouvelle, debut: e.target.value })} />
              </div>
              <div>
                <label htmlFor="fermeture-fin" className={labelCls}>Dernier jour de fermeture</label>
                <input id="fermeture-fin" data-testid="fermeture-fin" type="date" required className={inputCls}
                  value={nouvelle.fin} onChange={(e) => setNouvelle({ ...nouvelle, fin: e.target.value })} />
              </div>
            </div>
            <div>
              <label htmlFor="fermeture-motif" className={labelCls}>Motif (facultatif)</label>
              <input id="fermeture-motif" data-testid="fermeture-motif" className={inputCls}
                value={nouvelle.motif} onChange={(e) => setNouvelle({ ...nouvelle, motif: e.target.value })}
                placeholder="Ex. : Congés annuels" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="submit" data-testid="fermeture-valider" className={btnPrimaire}>Valider la fermeture</button>
              <button type="button" onClick={() => setNouvelle(null)}
                className="bg-brand-dark text-white font-heading font-semibold uppercase tracking-wider px-6 py-4 text-lg">
                Annuler
              </button>
            </div>
          </form>
        ) : (
          <button data-testid="fermeture-ajouter" onClick={() => setNouvelle({ debut: "", fin: "", motif: "" })}
            className={`${btnPrimaire} inline-flex items-center gap-2`}>
            <Plus size={22} aria-hidden="true" /> Signaler une fermeture exceptionnelle
          </button>
        )}
      </div>
    </div>
  );
}
